import React, { useState, useEffect } from 'react'
import { BOOKMAKER_LOGOS, BOOKMAKERS_BY_REGION } from '../config/bookmakers'
import { ALL_SPORTS, SPORT_DISPLAY_NAMES, SPORT_HIERARCHY } from '../config/sports'
import { MARKET_TYPE_HIERARCHY } from '../config/markets'
import { MarketCategory } from '../EV_NEW_PAGE/FilterPanel'

export const MIDDLES_DEFAULT_FILTERS = {
  sports:        new Set(ALL_SPORTS),
  bookmakers:    new Set(BOOKMAKERS_BY_REGION.AU),
  excludedMarketTypes: new Set(),
  odds:          [1, 6],
  ql:            [-10, 8],
  middleRange:   [0.5, 5],
  effectiveOdds: [5, 1000],
  hoursUntil:    [0, 24],
}
export const MIDDLES_DEFAULT_STAKE = 100
export const MIDDLES_DEFAULT_ROUND = '1'

// ─── Shared input components ──────────────────────────────────────────────────

const NumericInput = ({ value, onChange, defaultValue = 0, ...props }) => {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => { setDraft(String(value)) }, [value])
  return (
    <input
      {...props}
      type="number"
      className="bc-range-input"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        const n = parseFloat(draft)
        const final = isNaN(n) ? defaultValue : n
        setDraft(String(final))
        onChange(final)
      }}
    />
  )
}

const OddsInput = ({ value, onChange, ...props }) => {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => { setDraft(String(value)) }, [value])
  const step = (v, dir) => { const s = v < 2 ? 0.1 : 0.5; return Math.round((v + dir * s) * 10) / 10 }
  return (
    <input
      {...props}
      type="number"
      className="bc-range-input"
      value={draft}
      step="any"
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        const n = parseFloat(draft)
        const final = isNaN(n) ? value : n
        setDraft(String(final))
        onChange(final)
      }}
      onKeyDown={e => {
        if (e.key === 'ArrowUp')   { e.preventDefault(); const v = step(parseFloat(draft) || value,  1); setDraft(String(v)); onChange(v) }
        if (e.key === 'ArrowDown') { e.preventDefault(); const v = step(parseFloat(draft) || value, -1); setDraft(String(v)); onChange(v) }
      }}
    />
  )
}

function CollapsibleSection({ label, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="bc-filter-section">
      <button className="bc-collapsible-header" onClick={() => setOpen(o => !o)}>
        <span className="bc-filter-label" style={{ margin: 0 }}>{label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>
      {open && <div className="bc-collapsible-body">{children}</div>}
    </div>
  )
}

const SPORT_GROUPS = Object.entries(SPORT_HIERARCHY).map(([label, { leagues }]) => ({ label, sports: leagues }))
const REGION_LABELS = { AU: 'Australia', US: 'United States', EU: 'Europe', EXCHANGE: 'Exchanges' }

function SportGroup({ group, filters, setFilters }) {
  const [open, setOpen] = useState(false)
  const allOn  = group.sports.every(s => filters.sports.has(s))
  const allOff = group.sports.every(s => !filters.sports.has(s))

  const toggleGroup = (e) => {
    e.stopPropagation()
    setFilters(prev => {
      const next = new Set(prev.sports)
      allOn
        ? group.sports.forEach(s => next.delete(s))
        : group.sports.forEach(s => next.add(s))
      return { ...prev, sports: next }
    })
  }

  const toggleLeague = (s) => {
    setFilters(prev => {
      const next = new Set(prev.sports)
      next.has(s) ? next.delete(s) : next.add(s)
      return { ...prev, sports: next }
    })
  }

  return (
    <div className={`bc-sgroup${open ? ' open' : ''}`}>
      <div className="bc-sgroup-header" onClick={() => setOpen(o => !o)}>
        <div className="bc-sgroup-left">
          <input
            type="checkbox"
            checked={allOn}
            ref={el => { if (el) el.indeterminate = !allOn && !allOff }}
            onChange={toggleGroup}
            onClick={e => e.stopPropagation()}
          />
          <span className="bc-sgroup-name">{group.label}</span>
        </div>
        <div className="bc-sgroup-right">
          <svg className="bc-sgroup-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
          </svg>
        </div>
      </div>
      {open && (
        <div className="bc-sgroup-items">
          {group.sports.map(s => (
            <label key={s} className="bc-check-item">
              <input type="checkbox" checked={filters.sports.has(s)} onChange={() => toggleLeague(s)} />
              {SPORT_DISPLAY_NAMES[s] || s}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main FilterPanel ─────────────────────────────────────────────────────────

export default function FilterPanel({
  isOpen, onClose,
  filters, setFilters,
  totalStake, setTotalStake,
  roundStakes, setRoundStakes,
  savedFilters = [], activeFilterId,
  filterName = '', setFilterName,
  onSaveFilter, onLoadFilter, onDeleteFilter, onToggleDefault,
  onReset,
  // Mobile sort props (optional)
  sortBy, setSortBy, sortDir, setSortDir,
}) {
  const [activeRegion, setActiveRegion] = useState('AU')

  if (!isOpen) return null

  const toggleBookmaker = (b) => {
    setFilters(prev => {
      const next = new Set(prev.bookmakers)
      const nextReq = new Set(prev.requiredBookmakers)
      if (next.has(b)) { next.delete(b); nextReq.delete(b) }
      else next.add(b)
      return { ...prev, bookmakers: next, requiredBookmakers: nextReq }
    })
  }

  // Pin = "must include": every opportunity must contain at least one required book.
  // Marking required auto-includes the book.
  const toggleRequired = (b) => {
    setFilters(prev => {
      const nextReq = new Set(prev.requiredBookmakers)
      const next = new Set(prev.bookmakers)
      if (nextReq.has(b)) { nextReq.delete(b) }
      else { nextReq.add(b); next.add(b) }
      return { ...prev, bookmakers: next, requiredBookmakers: nextReq }
    })
  }

  // Market types are a blacklist of display names (same as the EV page), so
  // markets added later show up ticked.
  const isExcluded = (set, v) => set.has(v)
  const toggle = (key, v) => setFilters(prev => {
    const next = new Set(prev[key])
    next.has(v) ? next.delete(v) : next.add(v)
    return { ...prev, [key]: next }
  })

  const selectRegion = (region) => setFilters(prev => {
    const next = new Set(prev.bookmakers)
    BOOKMAKERS_BY_REGION[region].forEach(b => next.add(b))
    return { ...prev, bookmakers: next }
  })

  const clearRegion = (region) => setFilters(prev => {
    const next = new Set(prev.bookmakers)
    BOOKMAKERS_BY_REGION[region].forEach(b => next.delete(b))
    return { ...prev, bookmakers: next }
  })

  return (
    <>
      <div className="bc-overlay" onClick={onClose} />
      <div className="bc-filter-panel">

        {/* Header */}
        <div className="bc-panel-header">
          <p className="bc-panel-title">Filters</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="bc-btn bc-reset-header-btn" onClick={onReset} style={{ fontSize: '12px', padding: '5px 10px' }}>
              Reset to default
            </button>
            <button className="bc-panel-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="bc-panel-body">

          {/* ── Saved Filters ──────────────────────────────────────────── */}
          {onLoadFilter && (
            <div className="bc-filter-section">
              <span className="bc-filter-label">Saved Filters</span>
              {savedFilters.length === 0 ? (
                <p className="bc-saved-empty">Set your filters below, then save them at the bottom.</p>
              ) : (
                <div className="bc-saved-list">
                  {savedFilters.map(f => (
                    <div
                      key={f.id}
                      className={`bc-saved-item${f.isDefault ? ' is-default' : ''}${activeFilterId === f.id ? ' is-active' : ''}`}
                      onClick={() => onLoadFilter(f)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="bc-saved-load">{f.name}</span>
                      <div className="bc-saved-actions">
                        <button
                          className={`bc-saved-star${f.isDefault ? ' active' : ''}`}
                          onClick={e => { e.stopPropagation(); onToggleDefault(f.id) }}
                          title={f.isDefault ? 'Remove default' : 'Set as default'}
                        >★</button>
                        <button
                          className="bc-saved-delete"
                          onClick={e => { e.stopPropagation(); onDeleteFilter(f.id) }}
                          title="Delete"
                        >×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Sports ─────────────────────────────────────────────────── */}
          <CollapsibleSection label="Sports">
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
              <button className="bc-bulk-btn select" onClick={() => setFilters(prev => ({ ...prev, sports: new Set(ALL_SPORTS) }))}>Select All</button>
              <button className="bc-bulk-btn clear" onClick={() => setFilters(prev => ({ ...prev, sports: new Set() }))}>Clear All</button>
            </div>
            <div className="bc-sgroup-list">
              {SPORT_GROUPS.map(group => (
                <SportGroup key={group.label} group={group} filters={filters} setFilters={setFilters} />
              ))}
            </div>
          </CollapsibleSection>

          {/* ── Bookmakers ─────────────────────────────────────────────── */}
          <CollapsibleSection label="Bookmakers">
            <div className="bc-region-tabs">
              {Object.keys(BOOKMAKERS_BY_REGION).map(region => (
                <button
                  key={region}
                  className={`bc-region-tab${activeRegion === region ? ' active' : ''}`}
                  onClick={() => setActiveRegion(region)}
                >
                  {REGION_LABELS[region]}
                </button>
              ))}
            </div>
            <div className="bc-book-bulk-btns">
              <button className="bc-bulk-btn select" onClick={() => selectRegion(activeRegion)}>Select All</button>
              <button className="bc-bulk-btn clear" onClick={() => clearRegion(activeRegion)}>Clear All</button>
            </div>
            <div className="bc-book-list">
              {BOOKMAKERS_BY_REGION[activeRegion].map(b => {
                const active = filters.bookmakers.has(b)
                const required = filters.requiredBookmakers?.has(b)
                return (
                  <button
                    key={b}
                    className={`bc-book-row${active ? ' active' : ''}${required ? ' required' : ''}`}
                    onClick={() => toggleBookmaker(b)}
                  >
                    <div className="bc-book-row-logo-wrap">
                      {BOOKMAKER_LOGOS[b]
                        ? <img src={BOOKMAKER_LOGOS[b]} alt={b} className="bc-book-row-logo" />
                        : <span className="bc-book-row-abbr">{b.slice(0, 2)}</span>}
                    </div>
                    <span className="bc-book-row-name">{b}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      className={`bc-book-pin${required ? ' active' : ''}`}
                      data-tooltip="Must include this bookmaker in every opportunity"
                      onClick={e => { e.stopPropagation(); toggleRequired(b) }}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleRequired(b) } }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill={required ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 17v5"/><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/>
                      </svg>
                    </span>
                  </button>
                )
              })}
            </div>
          </CollapsibleSection>

          {/* ── Market Types ───────────────────────────────────────────── */}
          <CollapsibleSection label="Market Types">
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
              <button className="bc-bulk-btn select" onClick={() => setFilters(prev => ({ ...prev, excludedMarketTypes: new Set() }))}>Select All</button>
              <button className="bc-bulk-btn clear" onClick={() => setFilters(prev => ({ ...prev, excludedMarketTypes: new Set(Object.values(MARKET_TYPE_HIERARCHY).flatMap(sub => Object.values(sub).flat())) }))}>Clear All</button>
            </div>
            <div className="bc-sgroup-list">
              {Object.entries(MARKET_TYPE_HIERARCHY).map(([categoryLabel, subcategories]) => (
                <MarketCategory
                  key={categoryLabel}
                  label={categoryLabel}
                  subcategories={subcategories}
                  filters={filters}
                  setFilters={setFilters}
                  toggle={toggle}
                  isExcluded={isExcluded}
                />
              ))}
            </div>
          </CollapsibleSection>

          {/* ── QL (Qualifying Loss %) ──────────────────────────────────── */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">QL %</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <NumericInput
                  value={filters.ql[0]}
                  defaultValue={-10}
                  onChange={v => setFilters(prev => ({ ...prev, ql: [v, prev.ql[1]] }))}
                />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <NumericInput
                  value={filters.ql[1]}
                  defaultValue={8}
                  onChange={v => setFilters(prev => ({ ...prev, ql: [prev.ql[0], v] }))}
                />
              </div>
            </div>
          </div>

          {/* ── Odds ───────────────────────────────────────────────────── */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Odds</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <OddsInput
                  value={filters.odds[0]}
                  onChange={v => setFilters(prev => ({ ...prev, odds: [v, prev.odds[1]] }))}
                />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <OddsInput
                  value={filters.odds[1]}
                  onChange={v => setFilters(prev => ({ ...prev, odds: [prev.odds[0], v] }))}
                />
              </div>
            </div>
          </div>

          {/* ── Middle Range Size ──────────────────────────────────────── */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Middle Range Size</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <NumericInput
                  value={filters.middleRange[0]}
                  defaultValue={0.5}
                  onChange={v => setFilters(prev => ({ ...prev, middleRange: [v, prev.middleRange[1]] }))}
                />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <NumericInput
                  value={filters.middleRange[1]}
                  defaultValue={5}
                  onChange={v => setFilters(prev => ({ ...prev, middleRange: [prev.middleRange[0], v] }))}
                />
              </div>
            </div>
          </div>

          {/* ── Effective Odds ─────────────────────────────────────────── */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Effective Odds</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <OddsInput
                  value={filters.effectiveOdds[0]}
                  onChange={v => setFilters(prev => ({ ...prev, effectiveOdds: [v, prev.effectiveOdds[1]] }))}
                />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <OddsInput
                  value={filters.effectiveOdds[1]}
                  onChange={v => setFilters(prev => ({ ...prev, effectiveOdds: [prev.effectiveOdds[0], v] }))}
                />
              </div>
            </div>
          </div>

          {/* ── Starts In ──────────────────────────────────────────────── */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Starts In (hours)</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <NumericInput
                  value={filters.hoursUntil[0]}
                  defaultValue={0}
                  onChange={v => setFilters(prev => ({ ...prev, hoursUntil: [v, prev.hoursUntil[1]] }))}
                />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <NumericInput
                  value={filters.hoursUntil[1]}
                  defaultValue={24}
                  onChange={v => setFilters(prev => ({ ...prev, hoursUntil: [prev.hoursUntil[0], v] }))}
                />
              </div>
            </div>
          </div>

          {/* ── Stake Settings ─────────────────────────────────────────── */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Stake Settings</span>
            <div style={{ marginBottom: '10px' }}>
              <span className="bc-range-sublabel" style={{ display: 'block', marginBottom: '5px' }}>Total Stake ($)</span>
              <NumericInput
                value={totalStake}
                defaultValue={100}
                min={1}
                onChange={v => setTotalStake(v)}
              />
            </div>
            <div>
              <span className="bc-range-sublabel" style={{ display: 'block', marginBottom: '5px' }}>Rounding</span>
              <select className="bc-select" value={roundStakes} onChange={e => setRoundStakes(e.target.value)}>
                <option value="none">No rounding</option>
                <option value="1">Round to $1</option>
                <option value="5">Round to $5</option>
              </select>
            </div>
          </div>

          {/* ── Sort (mobile only) ─────────────────────────────────────── */}
          {setSortBy && (
            <div className="bc-filter-section">
              <span className="bc-filter-label">Sort By</span>
              <select
                className="bc-select"
                value={`${sortBy}:${sortDir}`}
                onChange={e => {
                  const [col, dir] = e.target.value.split(':')
                  setSortBy(col); setSortDir(dir)
                }}
              >
                <option value="ql:desc">QL ↓ (High to Low)</option>
                <option value="ql:asc">QL ↑ (Low to High)</option>
                <option value="middle_range:desc">Range ↓ (High to Low)</option>
                <option value="middle_range:asc">Range ↑ (Low to High)</option>
                <option value="effective_odds:desc">Eff. Odds ↓ (High to Low)</option>
                <option value="effective_odds:asc">Eff. Odds ↑ (Low to High)</option>
                <option value="odds:desc">Odds ↓ (High to Low)</option>
                <option value="odds:asc">Odds ↑ (Low to High)</option>
                <option value="start_time:asc">Start Time ↑ (Soonest)</option>
                <option value="start_time:desc">Start Time ↓ (Latest)</option>
                <option value="sport:asc">Sport A–Z</option>
              </select>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="bc-panel-footer">
          {onSaveFilter && (
            <div className="bc-save-row">
              <input
                className="bc-range-input"
                placeholder="Filter name..."
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onSaveFilter(filterName)}
              />
              <button className="bc-btn" onClick={() => onSaveFilter(filterName)}>Save</button>
            </div>
          )}
          <div className="bc-footer-actions">
            <button className="bc-btn bc-reset-footer-btn" onClick={onReset}>Reset</button>
            <button className="bc-btn bc-apply-btn" onClick={onClose}>Apply</button>
          </div>
        </div>

      </div>
    </>
  )
}
